import { useRef } from "react";
import { DatePicker } from "@/components/ui/date-picker";
import type { InlineFieldProps } from "./types";
import { INLINE_INPUT_CLASS } from "./inline-field-styles";

/**
 * Inline date field editor wrapping the existing DatePicker component.
 *
 * The DatePicker renders its own Popover (uncontrolled).
 * - User clicks the trigger button → calendar opens
 * - Selecting a date fires onChange → we call onCommit
 * - Cancel: parent widget detects blur without commit and reverts to display mode
 *
 * Known limitation: No auto-open on mount (DatePicker Popover is uncontrolled).
 * User must click the trigger button after entering edit mode.
 */
export function InlineDatePicker({
  value,
  onCommit,
  onCancel,
}: InlineFieldProps) {
  const committedRef = useRef(false);

  const handleChange = (dateStr: string) => {
    if (committedRef.current) return;
    committedRef.current = true;

    const oldValue = String(value ?? "");
    if (dateStr !== oldValue) {
      onCommit(dateStr);
    } else {
      onCancel();
    }
  };

  return (
    <div
      onBlur={(e) => {
        // If focus leaves the date picker area entirely without a commit, cancel
        if (!committedRef.current && !e.currentTarget.contains(e.relatedTarget)) {
          onCancel();
        }
      }}
    >
      <DatePicker
        value={String(value ?? "")}
        onChange={handleChange}
        className={INLINE_INPUT_CLASS}
      />
    </div>
  );
}
